import type { JSX } from "react";
import { cn } from "./cn";
import { Icon } from "./Icon";

export type TaskStatusColor = "accent" | "blue" | "purple" | "green" | "amber" | "red" | "neutral";

export interface TaskStatusCardProps {
  /** Task title */
  readonly title: string;
  /** Status label (e.g. "Active", "In Progress") */
  readonly status: string;
  /** Status color */
  readonly statusColor: TaskStatusColor;
  /** Description after the status */
  readonly description: string;
  /** Show action icons (plus, clock, kebab) */
  readonly showActions?: boolean;
  /** Click handler */
  readonly onClick?: () => void;
  /** Extra className */
  readonly className?: string;
}

const statusColorMap: Readonly<Record<TaskStatusColor, string>> = {
  accent: "text-accent",
  blue: "text-blue",
  purple: "text-content-secondary",
  green: "text-content-secondary",
  amber: "text-content-secondary",
  red: "text-content-secondary",
  neutral: "text-content-secondary",
};

/**
 * Task card with title, action icons, and colored status text.
 * Matches B1 Pencil design — used for Tasks section.
 */
export function TaskStatusCard({
  title,
  status,
  statusColor,
  description,
  showActions = true,
  onClick,
  className,
}: TaskStatusCardProps): JSX.Element {
  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      className={cn(
        "flex flex-col gap-1.5 bg-surface-secondary rounded-[10px] px-4 py-3.5",
        "border border-edge",
        onClick && "cursor-pointer hover:bg-surface-hover transition-colors text-left w-full",
        className,
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="flex-1 text-sm font-semibold text-content truncate">{title}</span>
        {showActions ? (
          <div className="flex items-center gap-3 shrink-0 text-content-tertiary">
            <Icon name="plus" size={16} />
            <Icon name="clock-3" size={16} />
            <Icon name="ellipsis-vertical" size={16} />
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-1.5 text-xs">
        <span className={cn("font-medium", statusColorMap[statusColor])}>{status}</span>
        <span className="text-content-secondary">·</span>
        <span className="text-content-secondary">{description}</span>
      </div>
    </Component>
  );
}
